import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LeadPicker } from '../components/LeadPicker';
import {
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  TagPill,
  compactInputClass,
  inputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { formatDateTime, formatMoney, formatRelative, initials } from '../lib/format';
import { useChatRealtime } from '../lib/realtime';
import {
  chatApi,
  connectionsApi,
  departmentsApi,
  getLead,
  listAttendants,
  listLeadBusinesses,
  tags as tagsApi,
  type ConversationFilters,
} from '../lib/resources';
import type { ChatConversation, ChatMessage, ConversationStatus } from '../types';

const TABS: Array<[ConversationStatus | 'all', string]> = [
  ['all', 'Todas'],
  ['waiting', 'Aguardando'],
  ['opened', 'Em atendimento'],
  ['unstarted', 'Não iniciadas'],
  ['finished', 'Finalizadas'],
  ['error', 'Com erro'],
];

const STATUS_STYLE: Record<ConversationStatus, string> = {
  waiting: 'bg-amber-100 text-amber-700',
  opened: 'bg-emerald-100 text-emerald-700',
  unstarted: 'bg-slate-100 text-slate-600',
  finished: 'bg-slate-100 text-slate-500',
  error: 'bg-red-100 text-red-700',
  automation: 'bg-violet-100 text-violet-700',
};

const STATUS_LABEL: Record<ConversationStatus, string> = {
  waiting: 'Aguardando',
  opened: 'Em atendimento',
  unstarted: 'Não iniciada',
  finished: 'Finalizada',
  error: 'Erro',
  automation: 'Automação',
};

const FILTER_KEYS = [
  'departments',
  'attendants',
  'instances',
  'tags',
  'openWindow',
  'unread',
  'sort',
] as const;

const formatPhone = (digits: string) =>
  digits.startsWith('55') && digits.length >= 12
    ? `+55 ${digits.slice(2, 4)} ${digits.slice(4, -4)}-${digits.slice(-4)}`
    : `+${digits}`;

/**
 * Chat ao vivo (doc 00): ESPELHO só de leitura (Q3) — lista por status,
 * filtros, a conversa com remetente, hora e entrega, e o painel do lead.
 * Responder é pelo app do WhatsApp; aqui só Marcar como lida, Finalizar e
 * Transferir, que mexem só no espelho (Q7).
 */
export function ChatPage() {
  const [params, setParams] = useSearchParams();
  const online = useChatRealtime();
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState(params.get('search') ?? '');
  const selectedId = params.get('c');
  const status = params.get('status') ?? 'all';

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const filters: ConversationFilters = {
    status,
    search: params.get('search') ?? undefined,
    departments: params.get('departments') ?? undefined,
    attendants: params.get('attendants') ?? undefined,
    instances: params.get('instances') ?? undefined,
    tags: params.get('tags') ?? undefined,
    openWindow: params.get('openWindow') === 'last24h' ? 'last24h' : undefined,
    unread: params.get('unread') === 'true' || undefined,
    sort: (params.get('sort') as ConversationFilters['sort']) ?? undefined,
  };

  const listQuery = useInfiniteQuery({
    queryKey: ['chat', 'list', filters],
    queryFn: ({ pageParam }) => chatApi.list({ ...filters, skip: pageParam, take: 40 }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
  });
  const rows = (listQuery.data?.pages ?? []).flatMap((page) => page.data);
  const counts = listQuery.data?.pages[0]?.counts ?? {};
  const connections = useQuery({ queryKey: ['connections'], queryFn: connectionsApi.list });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const filterCount = FILTER_KEYS.filter((key) => params.get(key)).length;

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-80 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="space-y-2 border-b border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900">Chat ao vivo</h1>
            <span
              className={`text-[11px] ${online ? 'text-emerald-600' : 'text-slate-400'}`}
              title={online ? 'Atualiza sozinho' : 'Sem tempo real — recarregue para atualizar'}
            >
              ● {online ? 'ao vivo' : 'offline'}
            </span>
          </div>
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              update({ search: search.trim() || null });
            }}
          >
            <input
              className={`${compactInputClass} min-w-0 flex-1`}
              placeholder="Nome ou telefone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onBlur={() => update({ search: search.trim() || null })}
            />
            <Button onClick={() => setShowFilters(true)}>
              Filtros{filterCount ? ` (${filterCount})` : ''}
            </Button>
          </form>
          <div className="flex flex-wrap gap-1">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => update({ status: key === 'all' ? null : key })}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  status === key
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label} <span className="opacity-70">{counts[key] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
        <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
          {rows.map((row) => (
            <ConversationItem
              key={row._id}
              conversation={row}
              active={row._id === selectedId}
              connectionName={
                connections.data?.data.find((item) => item.id === row.instanceId)?.name
              }
              departmentColor={
                departments.data?.data.find((item) => item.id === row.departmentId)?.color ??
                null
              }
              onSelect={() => update({ c: row._id })}
            />
          ))}
          {listQuery.isLoading && (
            <li className="p-4 text-center text-xs text-slate-400">Carregando…</li>
          )}
          {!listQuery.isLoading && rows.length === 0 && (
            <li className="p-6 text-center text-xs text-slate-400">
              Nenhuma conversa com estes filtros.
            </li>
          )}
          {listQuery.hasNextPage && (
            <li className="p-2">
              <Button className="w-full" onClick={() => listQuery.fetchNextPage()}>
                Carregar mais
              </Button>
            </li>
          )}
        </ul>
        {listQuery.error && (
          <div className="p-3">
            <ErrorNote>
              {errorMessage(listQuery.error, 'Falha ao carregar as conversas')}
            </ErrorNote>
          </div>
        )}
      </aside>

      {selectedId ? (
        <ConversationView
          key={selectedId}
          conversationId={selectedId}
          onClosed={() => update({ c: null })}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center bg-slate-50 p-8">
          <EmptyState
            title="Escolha uma conversa"
            hint="O chat é um espelho do WhatsApp: as respostas são dadas pelo app do celular e aparecem aqui."
          />
        </div>
      )}

      {showFilters && (
        <ChatFilters params={params} onApply={update} onClose={() => setShowFilters(false)} />
      )}
    </div>
  );
}

function ConversationItem({
  conversation,
  active,
  connectionName,
  departmentColor,
  onSelect,
}: {
  conversation: ChatConversation;
  active: boolean;
  connectionName?: string;
  departmentColor: string | null;
  onSelect: () => void;
}) {
  const title =
    conversation.leadName ?? conversation.name ?? formatPhone(conversation.contactId);
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full gap-3 px-3 py-2.5 text-left ${active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
          {initials(title)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-slate-900">{title}</span>
            <span className="shrink-0 text-[10px] text-slate-400">
              {formatRelative(conversation.lastMessageDate)}
            </span>
          </span>
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-xs text-slate-500">
              {conversation.lastMessageReceived ? '' : 'Você: '}
              {conversation.lastMessagePreview}
            </span>
            {conversation.unreadCount > 0 && (
              <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 text-[10px] font-semibold text-white">
                {conversation.unreadCount}
              </span>
            )}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-400">
            {departmentColor && (
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: departmentColor }}
              />
            )}
            <span className={`rounded px-1 ${STATUS_STYLE[conversation.status]}`}>
              {STATUS_LABEL[conversation.status]}
            </span>
            {connectionName && <span className="truncate">{connectionName}</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

function ConversationView({
  conversationId,
  onClosed,
}: {
  conversationId: string;
  onClosed: () => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canTransfer = can('multiservice_supervisor', 'multiservice_attendant_supervisor');
  const [transferring, setTransferring] = useState(false);
  const conversation = useQuery({
    queryKey: ['chat', 'conversation', conversationId],
    queryFn: () => chatApi.get(conversationId),
  });
  const messages = useInfiniteQuery({
    queryKey: ['chat', 'messages', conversationId],
    queryFn: ({ pageParam }) => chatApi.messages(conversationId, pageParam, 50),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
  });
  const connections = useQuery({ queryKey: ['connections'], queryFn: connectionsApi.list });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['chat'] });
  const read = useMutation({
    mutationFn: () => chatApi.read(conversationId),
    onSuccess: refresh,
  });
  const finish = useMutation({
    mutationFn: () => chatApi.finish(conversationId),
    onSuccess: refresh,
  });

  if (conversation.isLoading)
    return (
      <div className="flex-1">
        <Loading />
      </div>
    );
  if (!conversation.data) {
    return (
      <div className="flex-1 p-6">
        <ErrorNote>{errorMessage(conversation.error, 'Conversa não encontrada')}</ErrorNote>
        <Button className="mt-3" onClick={onClosed}>
          Voltar
        </Button>
      </div>
    );
  }
  const data = conversation.data;
  const ordered = [...(messages.data?.pages ?? []).flatMap((page) => page.data)].reverse();
  const title = data.leadName ?? data.name ?? formatPhone(data.contactId);
  const department = departments.data?.data.find((item) => item.id === data.departmentId);
  const attendant = attendants.data?.data.find((item) => item.userId === data.attendantId);

  return (
    <>
      <section className="flex min-w-0 flex-1 flex-col bg-slate-50">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{title}</p>
            <p className="text-xs text-slate-500">
              {formatPhone(data.contactId)}
              {data.threadCode ? ` · Atendimento #${data.threadCode}` : ''}
              {' · '}
              {connections.data?.data.find((item) => item.id === data.instanceId)?.name ??
                'Conexão'}
              {' · '}
              {department?.name ?? 'Sem departamento'}
              {' · '}
              {attendant?.name ?? 'Sem atendente'}
            </p>
          </div>
          <div className="flex gap-2">
            {data.unreadCount > 0 && (
              <Button onClick={() => read.mutate()} disabled={read.isPending}>
                Marcar como lida
              </Button>
            )}
            {canTransfer && <Button onClick={() => setTransferring(true)}>Transferir</Button>}
            {!data.finished && (
              <Button
                variant="primary"
                onClick={() => finish.mutate()}
                disabled={finish.isPending}
              >
                Finalizar
              </Button>
            )}
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
          {messages.hasNextPage && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => messages.fetchNextPage()}
                className="text-xs text-slate-500 hover:underline"
              >
                Carregar anteriores
              </button>
            </div>
          )}
          {ordered.map((message) => (
            <MessageBubble key={message._id} message={message} />
          ))}
          {!messages.isLoading && ordered.length === 0 && (
            <p className="text-center text-xs text-slate-400">Nenhuma mensagem.</p>
          )}
        </div>
        <footer className="border-t border-slate-200 bg-white px-4 py-2 text-center text-xs text-slate-500">
          Somente leitura — responda pelo app do WhatsApp; a resposta aparece aqui.
        </footer>
        {(read.error || finish.error) && (
          <div className="p-3">
            <ErrorNote>{errorMessage(read.error ?? finish.error)}</ErrorNote>
          </div>
        )}
      </section>
      <LeadPanel conversation={data} />
      {transferring && (
        <TransferModal
          conversation={data}
          onClose={() => setTransferring(false)}
          onDone={refresh}
        />
      )}
    </>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const mine = !message.received;
  const ticks =
    message.status === 'read'
      ? '✓✓ lida'
      : message.status === 'delivered'
        ? '✓✓'
        : message.status === 'sent'
          ? '✓'
          : '';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          mine
            ? 'rounded-br-sm bg-emerald-50 text-slate-800'
            : 'rounded-bl-sm bg-white text-slate-800'
        }`}
      >
        {message.senderType === 'external' && (
          <p className="mb-0.5 text-[10px] font-semibold uppercase text-violet-600">Externo</p>
        )}
        {message.attachments.map((attachment, index) => (
          <Attachment key={index} attachment={attachment} />
        ))}
        {message.location && (
          <a
            className="block text-sky-700 underline"
            href={`https://maps.google.com/?q=${message.location.latitude},${message.location.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            📍 {message.location.name ?? 'Ver localização'}
          </a>
        )}
        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
        {!message.body && message.attachments.length === 0 && !message.location && (
          <p className="italic text-slate-400">({message.kind})</p>
        )}
        <p className="mt-1 text-right text-[10px] text-slate-400">
          {formatDateTime(message.sentAt ?? message.createdAt)} {mine && ticks}
        </p>
        {message.status === 'failed' && (
          <p className="text-[11px] text-red-600">
            Não entregue: {message.errorMessage ?? 'erro'}
          </p>
        )}
      </div>
    </div>
  );
}

function Attachment({ attachment }: { attachment: ChatMessage['attachments'][number] }) {
  const blob = useQuery({
    queryKey: ['chat', 'media', attachment.url],
    queryFn: () => chatApi.mediaUrl(attachment.url),
    enabled: Boolean(attachment.url),
    staleTime: Infinity,
  });
  if (!attachment.url) {
    return (
      <p className="mb-1 text-xs text-slate-400">
        📎 {attachment.fileName} — {attachment.error ?? 'não baixado'}
      </p>
    );
  }
  if (!blob.data)
    return <p className="mb-1 text-xs text-slate-400">Carregando {attachment.fileName}…</p>;
  if (attachment.type === 'IMAGE')
    return (
      <img src={blob.data} alt={attachment.fileName} className="mb-1 max-h-64 rounded-lg" />
    );
  if (attachment.type === 'AUDIO')
    return <audio controls src={blob.data} className="mb-1 max-w-full" />;
  if (attachment.type === 'VIDEO')
    return <video controls src={blob.data} className="mb-1 max-h-64 rounded-lg" />;
  return (
    <a
      href={blob.data}
      download={attachment.fileName}
      className="mb-1 block text-sky-700 underline"
    >
      📄 {attachment.fileName}
    </a>
  );
}

/** Painel do lead: ficha compacta, ou "Lead não encontrado" com Criar / Atribuir (Q4). */
function LeadPanel({ conversation }: { conversation: ChatConversation }) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const lead = useQuery({
    queryKey: ['lead', conversation.leadId],
    queryFn: () => getLead(conversation.leadId!),
    enabled: Boolean(conversation.leadId),
  });
  const businesses = useQuery({
    queryKey: ['lead-businesses', conversation.leadId],
    queryFn: () => listLeadBusinesses(conversation.leadId!),
    enabled: Boolean(conversation.leadId),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['chat'] });
  const create = useMutation({
    mutationFn: () => chatApi.createLead(conversation._id),
    onSuccess: refresh,
  });
  const attach = useMutation({
    mutationFn: (leadId: string) => chatApi.attachLead(conversation._id, leadId),
    onSuccess: refresh,
  });

  return (
    <aside className="w-80 shrink-0 overflow-y-auto border-l border-slate-200 bg-white p-4">
      {!conversation.leadId ? (
        <div className="space-y-4">
          <div>
            <p className="font-semibold text-slate-900">Lead não encontrado</p>
            <p className="text-xs text-slate-500">
              Nenhum lead com o telefone {formatPhone(conversation.contactId)}.
            </p>
          </div>
          <Button
            variant="primary"
            className="w-full"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            Criar lead
          </Button>
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-600">Atribuir a lead existente</p>
            <LeadPicker value={picked} onChange={setPicked} placeholder="Buscar lead…" />
            <Button
              className="w-full"
              disabled={!picked || attach.isPending}
              onClick={() => picked && attach.mutate(picked.id)}
            >
              Atribuir
            </Button>
          </div>
          {(create.error || attach.error) && (
            <ErrorNote>{errorMessage(create.error ?? attach.error)}</ErrorNote>
          )}
        </div>
      ) : lead.isLoading ? (
        <Loading />
      ) : lead.data ? (
        <div className="space-y-4">
          <div>
            <p className="text-lg font-semibold text-slate-900">{lead.data.name}</p>
            <p className="text-xs text-slate-500">
              {lead.data.phone ?? formatPhone(conversation.contactId)}
            </p>
            {lead.data.email && <p className="text-xs text-slate-500">{lead.data.email}</p>}
          </div>
          {lead.data.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {lead.data.tags.map((tag) => (
                <TagPill key={tag.id} tag={tag} />
              ))}
            </div>
          )}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Negócios</p>
            <ul className="space-y-1.5">
              {(businesses.data ?? []).slice(0, 5).map((business) => (
                <li
                  key={business.id}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs"
                >
                  <Link to={`/negocios/${business.id}`} className="font-medium hover:underline">
                    #{business.code}
                  </Link>{' '}
                  · {business.stage?.name ?? '—'} · {formatMoney(business.total)}
                </li>
              ))}
              {(businesses.data ?? []).length === 0 && (
                <li className="text-xs text-slate-400">Nenhum negócio.</li>
              )}
            </ul>
          </div>
          <Link
            to={`/leads/${lead.data.id}`}
            className="block text-center text-sm font-medium text-slate-700 hover:underline"
          >
            Abrir ficha completa →
          </Link>
          {conversation.sourceReferral?.sourceUrl && (
            <p className="text-[11px] text-slate-400">
              Veio de anúncio: {conversation.sourceReferral.sourceUrl}
            </p>
          )}
        </div>
      ) : (
        <ErrorNote>{errorMessage(lead.error, 'Lead não encontrado')}</ErrorNote>
      )}
    </aside>
  );
}

function TransferModal({
  conversation,
  onClose,
  onDone,
}: {
  conversation: ChatConversation;
  onClose: () => void;
  onDone: () => void;
}) {
  const [departmentId, setDepartmentId] = useState(conversation.departmentId ?? '');
  const [attendantId, setAttendantId] = useState(conversation.attendantId ?? '');
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const save = useMutation({
    mutationFn: () =>
      chatApi.transfer(conversation._id, {
        departmentId: departmentId || null,
        attendantId: attendantId || null,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Modal title="Transferir conversa" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Departamento">
          <select
            className={inputClass}
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Sem departamento</option>
            {(departments.data?.data ?? [])
              .filter((item) => item.isActive)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Atendente">
          <select
            className={inputClass}
            value={attendantId}
            onChange={(event) => setAttendantId(event.target.value)}
          >
            <option value="">Sem atendente</option>
            {(attendants.data?.data ?? []).map((item) => (
              <option key={item.id} value={item.userId}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-slate-500">
          A transferência muda só o espelho; o contato não é avisado.
        </p>
        {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Transferir
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ChatFilters({
  params,
  onApply,
  onClose,
}: {
  params: URLSearchParams;
  onApply: (changes: Record<string, string | null>) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) ?? ''])),
  );
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const connections = useQuery({ queryKey: ['connections'], queryFn: connectionsApi.list });
  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const set = (key: string, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const select = (
    key: string,
    label: string,
    options: Array<{ id: string; name: string }>,
    extra?: [string, string],
  ) => (
    <Field label={label}>
      <select
        className={inputClass}
        value={draft[key]}
        onChange={(event) => set(key, event.target.value)}
      >
        <option value="">Todos</option>
        {extra && <option value={extra[0]}>{extra[1]}</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <Modal title="Filtros do chat" onClose={onClose}>
      <div className="grid gap-3 sm:grid-cols-2">
        {select('departments', 'Departamento', departments.data?.data ?? [])}
        {select(
          'attendants',
          'Atendente',
          (attendants.data?.data ?? []).map((item) => ({ id: item.userId, name: item.name })),
          ['none', 'Sem atendente'],
        )}
        {select('instances', 'Conexão', connections.data?.data ?? [])}
        {select('tags', 'Tag do lead', tags.data?.data ?? [])}
        <Field label="Ordem">
          <select
            className={inputClass}
            value={draft.sort}
            onChange={(event) => set('sort', event.target.value)}
          >
            <option value="">Mais recentes</option>
            <option value="oldest">Mais antigas</option>
            <option value="unread">Não lidas primeiro</option>
          </select>
        </Field>
        <div className="space-y-2 pt-6 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.openWindow === 'last24h'}
              onChange={(event) => set('openWindow', event.target.checked ? 'last24h' : '')}
            />
            Janela de 24h aberta
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.unread === 'true'}
              onChange={(event) => set('unread', event.target.checked ? 'true' : '')}
            />
            Só não lidas
          </label>
        </div>
      </div>
      <div className="mt-5 flex justify-between">
        <Button
          variant="ghost"
          onClick={() => setDraft(Object.fromEntries(FILTER_KEYS.map((key) => [key, ''])))}
        >
          Limpar
        </Button>
        <div className="flex gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={() => {
              onApply(
                Object.fromEntries(
                  Object.entries(draft).map(([key, value]) => [key, value || null]),
                ),
              );
              onClose();
            }}
          >
            Aplicar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
